<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container">
        <div class="pagination-container">
          <el-pagination
            :disabled="isLoading"
            :current-page="listQuery.page"
            :page-sizes="listQuery.sizes"
            :page-size="listQuery.size"
            :total="listQuery.total"
            layout="total, sizes, pager, jumper"
            background
            size="mini"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
        <div class="button-container">
          <el-button
            :disabled="isLoading"
            icon="el-icon-close"
            size="mini"
            @click="resetFiliter"
          >{{ $t('button.resetFilter') }}</el-button>
          <el-button
            :disabled="isLoading"
            type="primary"
            icon="el-icon-search"
            size="mini"
            @click="handleSearch"
          >{{ $t('button.search') }}</el-button>
        </div>
      </div>

      <div class="filter-wrapper d-flex pt-2 pl-1">
        <label-wrapper
          class="mr-2"
          label="Valid"
        >
          <el-select
            v-model="filter.is_valid"
            :placeholder="$t('placeholder.select')"
          >
            <el-option
              v-for="item in validOptions"
              :key="item.key"
              :label="item.label"
              :value="item.key"
            />
          </el-select>
        </label-wrapper>

        <label-wrapper
          class="mr-2"
          label="Settled"
        >
          <el-select
            v-model="filter.is_settle"
            :placeholder="$t('placeholder.select')"
          >
            <el-option
              v-for="item in settleOptions"
              :key="item.key"
              :label="item.label"
              :value="item.key"
            />
          </el-select>
        </label-wrapper>
      </div>
    </template>

    <el-table
      v-loading="isLoading"
      :data="list"
      stripe
      border
    >
      <el-table-column
        v-for="{ type, ...column } in columnList"
        :key="type"
        :prop="type"
        :label="column.title || $t(`table.${type}`)"
        v-bind="column.custom"
      >
        <template #default="{ row }">
          <template v-if="type.startsWith('is_')">
            <template v-if="row[type] === null">
              N/A
            </template>
            <template v-else-if="row[type]">
              <font-awesome-icon icon="check" />
            </template>
            <template v-else>
              <font-awesome-icon icon="times" />
            </template>
          </template>

          <template v-else-if="type === 'uploads'">
            <div class="d-flex flex-direction-column">
              <template v-for="(upload, index) in row[type]">
                <el-popover
                  :key="index"
                  placement="left"
                  width="440"
                  trigger="hover"
                >
                  <a
                    slot="reference"
                    :href="upload.image"
                    target="_blank"
                  >Image {{ index + 1 }}</a>
                  <a
                    :href="upload.image"
                    target="_blank"
                  >
                    <img
                      :src="upload.image"
                      alt=""
                      style="max-width: 400px;"
                    >
                  </a>
                </el-popover>
              </template>
            </div>
          </template>

          <template v-else>
            {{ row[type] }}
          </template>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('table.action')"
        fixed="right"
        align="center"
        width="60"
      >
        <template #default="{ row }">
          <edit-button
            @click="handleEditAppeal(row)"
          />
        </template>
      </el-table-column>
    </el-table>

    <appeal-detail-dialog
      :data="selectedAppeal"
      :is-open="isAppealDialogOpen"
      :user-id="userId"
      @close="handleCloseAppealDialog"
    />
  </sticky-header-layout>
</template>

<script>
import { mapGetters } from 'vuex'

import EditButton from '../../components/buttons/EditButton.vue'
import LabelWrapper from '../../components/form/LabelWrapper.vue'
import AppealDetailDialog from './components/AppealDetailDialog.vue'

import useTablePaging from '../../mixins/useTablePaging'
import {
  columnList,
  validOptions,
  settleOptions,
} from '../../const/goxip-circle/appeal'
import { OrderAppealsAPI } from '../../api/memberships'
import { formatDatetime } from '../../utils/datetime'

export default {
  name: 'GoxipCircleOrderAppealList',
  components: {
    EditButton,
    AppealDetailDialog,
    LabelWrapper,
  },
  mixins: [useTablePaging],
  data() {
    return {
      isLoading: false,

      selectedAppeal: null,
      isAppealDialogOpen: false,

      columnList,
      validOptions,
      settleOptions,

      filter: this.getDefaultFilters(),
      prevFilter: null,
    }
  },
  computed: {
    ...mapGetters({
      userId: 'user/id',
    }),
  },
  mounted() {
    this.getList()
  },
  methods: {
    async getList() {
      this.isLoading = true
      try {
        const filter = this.getListCurrentFilter()
        const { data: newList, pagination } = await OrderAppealsAPI.getList(filter)
        if (!newList) {
          this.list = []
        } else {
          this.list = newList.map((appeal) => {
            appeal.is_handling = !!appeal.handle_by
            appeal.order_date = formatDatetime({ date: appeal.order_date })
            appeal.is_settle = Boolean(appeal.is_settle)
            return appeal
          })
        }
        this.listQuery.total = parseInt(pagination.total_items, 0)
      } finally {
        this.isLoading = false
      }
    },
    getDefaultFilters() {
      return {
        is_valid: undefined,
        is_settle: undefined,
      }
    },
    getListCurrentFilter() {
      const page = JSON.stringify(this.prevFilter) !== JSON.stringify(this.filter)
        ? 1
        : this.listQuery.page
      this.prevFilter = { ...this.filter }
      return {
        ...this.filter,
        page,
        size: this.listQuery.size,
      }
    },
    resetFiliter() {
      this.filter = this.getDefaultFilters()
    },
    handleEditAppeal(data) {
      this.isAppealDialogOpen = true
      this.selectedAppeal = { ...data }
    },
    handleCloseAppealDialog(isSaveSuccess) {
      this.isAppealDialogOpen = false
      if (isSaveSuccess) {
        const index = this.list.findIndex(appeal => appeal.id === this.selectedAppeal.id)
        this.list[index] = this.selectedAppeal
      }
      this.selectedAppeal = null
    },
    handleSearch() {
      this.getList()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

.filter-wrapper {
  > * {
    flex-basis: 25%;
    flex-wrap: wrap;
  }
}

::v-deep {
  .cell {
    word-break: break-word;
    a {
      text-decoration: underline;
    }
  }
}
</style>
